import React, { useState, useEffect, useMemo } from 'react';
import { socketService } from '../utils/socket';
import { localStorageManager } from '../utils/localStorage';
import SocketNotification from '../components/SocketNotification';
import './LeaderboardPage.css';

interface Team {
  name: string;
  color?: string;
  image?: string;
  phases: Record<string, number>;
  total: number;
}

interface LeaderboardConfig {
  title: string;
  maxVisiblePositions?: number;
  autoFit?: boolean;
  phaseNames: Record<string, string>;
  teams: Team[];
}

const DEFAULT_CONFIG: LeaderboardConfig = {
  title: 'Clasificacion general',
  maxVisiblePositions: 10,
  autoFit: false,
  phaseNames: {
    phase1: 'R',
    phase2: 'A.C',
    phase3: 'C y L',
    phase4: 'P'
  },
  teams: []
};

// Suma fases tratando cualquier valor no numérico como 0, evitando NaN.
const safeSumPhases = (phases: Record<string, unknown>): number =>
  Object.values(phases).reduce<number>((sum, points) => {
    const n = Number(points);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

const LeaderboardPage: React.FC = () => {
  const [config, setConfig] = useState<LeaderboardConfig>(DEFAULT_CONFIG);
  // Cambia en cada "refresh" para forzar replay de la animación CSS.
  const [animationKey, setAnimationKey] = useState(0);

  // Cargar configuración inicial desde localStorage UNA sola vez al montar.
  // Antes este bloque estaba dentro de un effect con deps [config.teams] y,
  // como localStorageManager.getConfig() devuelve un objeto nuevo cada llamada,
  // creaba un bucle infinito: cargar → setConfig → deps cambian → cargar...
  useEffect(() => {
    try {
      const fullConfig = localStorageManager.getConfig();
      if (fullConfig.leaderboard) {
        setConfig(prev => ({ ...prev, ...(fullConfig.leaderboard as LeaderboardConfig) }));
      }
    } catch (error) {
      console.error('Error loading leaderboard config:', error);
    }
  }, []);

  // Listeners de socket — registrados UNA vez al montar.
  useEffect(() => {
    const handleConfigUpdate = (data: any) => {
      if (data?.section === 'leaderboard' && data?.data) {
        setConfig(data.data as LeaderboardConfig);
      }
    };

    const handleLeaderboardCommand = (data: any) => {
      const { command, payload } = data ?? {};
      switch (command) {
        case 'updatePhase':
          setConfig(prev => ({
            ...prev,
            teams: prev.teams.map(team => {
              if (team.name !== payload?.teamName) return team;
              const updatedPhases = { ...team.phases, [payload.phase]: payload.points };
              return { ...team, phases: updatedPhases, total: safeSumPhases(updatedPhases) };
            })
          }));
          break;
        case 'addPhasePoints':
          setConfig(prev => ({
            ...prev,
            teams: prev.teams.map(team => {
              if (team.name !== payload?.teamName) return team;
              const current = (team.phases as any)[payload.phase] || 0;
              const updatedPhases = { ...team.phases, [payload.phase]: current + payload.points };
              return { ...team, phases: updatedPhases, total: safeSumPhases(updatedPhases) };
            })
          }));
          break;
        case 'resetPoints':
          setConfig(prev => ({
            ...prev,
            teams: prev.teams.map(team => {
              const zeroedPhases = Object.keys(prev.phaseNames).reduce<Record<string, number>>((acc, key) => {
                acc[key] = 0;
                return acc;
              }, {});
              return { ...team, phases: zeroedPhases, total: 0 };
            })
          }));
          break;
        case 'refresh':
          setAnimationKey(k => k + 1);
          break;
      }
    };

    socketService.on('configUpdate', handleConfigUpdate);
    socketService.on('leaderboardCommand', handleLeaderboardCommand);
    return () => {
      socketService.off('configUpdate', handleConfigUpdate);
      socketService.off('leaderboardCommand', handleLeaderboardCommand);
    };
  }, []);

  // Derivado puro: no hay estado `animatedTeams` en cascada con setTimeouts.
  // El efecto visual de entrada ya lo hace la CSS via `animationDelay` en cada fila.
  const sortedTeams = useMemo(() => {
    return [...config.teams]
      .filter(team => team && team.name && team.name.trim() !== '')
      .sort((a, b) => b.total - a.total)
      .slice(0, config.maxVisiblePositions || 10);
  }, [config.teams, config.maxVisiblePositions]);

  const phaseEntries = useMemo(
    () => Object.entries(config.phaseNames ?? {}),
    [config.phaseNames]
  );

  // numTeams como variable CSS: el CSS hace todo el cálculo de tamaños vía calc()
  // y se recomputa automáticamente cuando cambia, sin necesidad de JS/observers.
  const numTeamsVar = Math.max(1, sortedTeams.length);
  const containerStyle: React.CSSProperties = {
    ['--lb-num-teams' as any]: numTeamsVar,
  };
  // Para tablas grandes, comprime el delay de la animación para no tardar varios segundos.
  const animationStep = sortedTeams.length > 15 ? 0.04 : 0.15;

  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1: return '1';
      case 2: return '2';
      case 3: return '3';
      default: return position;
    }
  };

  const getPositionClass = (position: number) => {
    switch (position) {
      case 1: return 'gold';
      case 2: return 'silver';
      case 3: return 'bronze';
      default: return '';
    }
  };

  return (
    <div className="leaderboard-page">
      <SocketNotification showOnMount={true} />
      <div className="leaderboard-container">
        <div className="leaderboard-header">
          <h1 className="leaderboard-title">{config.title}</h1>
          <p className="leaderboard-subtitle">Ranking por Fases</p>
        </div>

        <div className="leaderboard-content">
          {sortedTeams.length === 0 ? (
            <div className="loading-animation">
              <div className="loading-spinner"></div>
              <p>Cargando clasificación...</p>
            </div>
          ) : (
            <div
              className={`leaderboard-table-container${config.autoFit ? ' auto-fit' : ''}`}
              style={containerStyle}
            >
              <table
                key={animationKey}
                className="leaderboard-table"
                data-teams={sortedTeams.length}
              >
                <colgroup>
                  <col className="position-col" />
                  <col className="team-col" />
                  {phaseEntries.map(([key]) => (
                    <col key={key} className="phase-col" />
                  ))}
                  <col className="total-col" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="position-col">Pos</th>
                    <th className="team-col">Equipo</th>
                    {phaseEntries.map(([key, label]) => (
                      <th key={key} className="phase-col">{label}</th>
                    ))}
                    <th className="total-col">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTeams.map((team, index) => {
                    const position = index + 1;
                    return (
                      <tr
                        key={team.name}
                        className={`team-row ${getPositionClass(position)} animate-in`}
                        style={{
                          animationDelay: `${index * animationStep}s`,
                          backgroundColor: team.color ? `${team.color}` : 'transparent',
                          borderLeft: team.color ? `6px solid ${team.color}` : 'none'
                        }}
                      >
                        <td className="position-cell">
                          <div className="position-content">
                            <span className="position-icon">{getPositionIcon(position)}</span>
                          </div>
                        </td>
                        <td className="team-cell">
                          <div className="team-info">
                            <span className="team-name">{team.name}</span>
                          </div>
                        </td>
                        {phaseEntries.map(([key]) => (
                          <td key={key} className="phase-cell">
                            <span className="phase-points">{team.phases?.[key] ?? 0}</span>
                          </td>
                        ))}
                        <td className="total-cell">
                          <span className="total-points">{team.total}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
